<script setup lang="tsx">
import { ref, computed, onBeforeMount } from 'vue'
import http from '@/services/http'
import type { JiraDomainConfig, AddJiraDomainRequest, Response, Instance } from '@/models/api-generated'
import UserAutocomplete from '@/components/widgets/UserAutocomplete.vue'
import InstanceAutocomplete from '@/components/widgets/InstanceAutocomplete.vue'
import configEditor from '@/stores/jira-config-editor'
import axios from 'axios'
import toastStore from '@/stores/toast'
import { useI18n } from 'vue-i18n'
import { viewing } from '@/services/utils'

const { t } = useI18n()
const toaster = toastStore()

const defaultDomainConfig: JiraDomainConfig = {
	id: 0,
	domain: '',
	status: 0,
	isActive: false,
	email: '',
	apiToken: '',
	parentDomainId: 0,
	automaticImport: true,
}

const store = configEditor()
const newDomainConfig = ref<JiraDomainConfig>({ ...defaultDomainConfig })
const emit = defineEmits(['domain-added'])

const isInstanceValid = computed(() => !!newDomainConfig.value.instance)
const isAdminValid = computed(() => !!newDomainConfig.value.admin)

const isValid = computed(() => {
	return (
		!store.changeCount &&
		isDomainValid.value &&
		isEmailValid.value &&
		!!newDomainConfig.value.domain &&
		isInstanceValid.value &&
		isAdminValid.value &&
		(viewing.value || newDomainConfig.value.instance?.id !== 0)
	)
})

const isEmailValid = computed(() => {
	const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
	const email = newDomainConfig.value.email
	return !email || emailPattern.test(email)
})

const isDomainValid = computed(() => {
	const domain = newDomainConfig.value.domain
	const domainPattern = /^https:\/\/.+\/$/
	return !domain || domainPattern.test(domain)
})

const addDomain = async () => {
	if (!isValid.value) return

	const req: AddJiraDomainRequest = {
		domainConfig: newDomainConfig.value,
	}
	const data = await http.post<Response<JiraDomainConfig>>('/api/jira/addDomainConfig', req)
	if (data.status === axios.HttpStatusCode.Ok) {
		toaster.showSuccess(t('jira.domainAdded'))
		newDomainConfig.value = { ...defaultDomainConfig }
		emit('domain-added')
	}
}

onBeforeMount(() => {
	if (!viewing.value) return
	newDomainConfig.value.instance = { id: viewing.value } as Instance
})
</script>

<template>
	<form
		class="mt-4 needs-validation"
		:class="{ 'was-validated': isValid }"
		id="config-add-domain"
		@submit.prevent="addDomain"
		novalidate
		autocomplete="off"
	>
		<input name="disable-pwd-mgr-1" type="password" id="disable-pwd-mgr-1" style="display: none" value="disable-pwd-mgr-1" />
		<input name="disable-pwd-mgr-2" type="password" id="disable-pwd-mgr-2" style="display: none" value="disable-pwd-mgr-2" />
		<input name="disable-pwd-mgr-3" type="password" id="disable-pwd-mgr-3" style="display: none" value="disable-pwd-mgr-3" />

		<div class="row mb-3">
			<div class="col-md-2">
				<div class="form-floating w-100">
					<input
						id="add-domain-field"
						type="text"
						class="form-control form-control-sm"
						:class="{ 'is-invalid': !isDomainValid }"
						v-model="newDomainConfig.domain"
						required
					/>
					<label for="domain">{{ $t('jira.domain') }}</label>
					<div class="invalid-feedback">{{ $t('jira.invalidDomain') }}</div>
				</div>
			</div>

			<div v-if="!viewing" class="col-md-2">
				<div class="form-floating w-100">
					<InstanceAutocomplete id="add-instance-field" v-model="newDomainConfig.instance" :class="{ 'is-invalid': !isInstanceValid }" />
					<label for="organization">{{ $t('institution') }}</label>
					<div class="invalid-feedback">{{ $t('jira.fieldIsRequired') }}</div>
				</div>
			</div>

			<div class="col-md-2">
				<div class="form-floating w-100">
					<UserAutocomplete
						id="add-admin-field"
						v-model="newDomainConfig.admin"
						autocomplete="off"
						:class="{ 'is-invalid': !isAdminValid }"
					/>
					<label for="admin">{{ $t('jira.admin') }}</label>
					<div class="invalid-feedback">{{ $t('jira.fieldIsRequired') }}</div>
				</div>
			</div>

			<div class="col-md-2">
				<div class="form-floating w-100">
					<input
						id="add-token-field"
						v-model="newDomainConfig.apiToken"
						type="password"
						autocomplete="off"
						class="form-control form-control-sm"
					/>
					<label for="apiToken">{{ $t('jira.apiToken') }}</label>
				</div>
			</div>

			<div class="col-md-2">
				<div class="form-floating w-100">
					<input
						id="add-email-field"
						type="email"
						class="form-control form-control-sm"
						v-model="newDomainConfig.email"
						:class="{ 'is-invalid': !isEmailValid }"
					/>
					<label for="email">{{ $t('jira.email') }}</label>
					<div class="invalid-feedback">{{ $t('jira.invalidEmail') }}</div>
				</div>
			</div>

			<div class="col-md-2">
				<button
					id="add-jira-config-btn"
					type="submit"
					:disabled="!isValid"
					class="btn height"
					:class="isValid ? 'btn-outline-primary' : 'btn-outline-grey'"
				>
					{{ $t('jira.addNewDomain') }}
				</button>
			</div>
		</div>
	</form>
</template>

<style lang="sass" scoped>
.form-floating
  margin-bottom: 1rem

.height
  min-height: 55px
</style>
